/* Product photographs roll on a cylinder inside one frame, the same for every
   product (Ahtisam, 27 September 2026): the shape of razpro-p50's first photograph
   (1920 x 1078), at the same share of the hero. Each photograph is fitted to it by
   what it shows (fitFor): a product on a plain ground is shown whole and the gap
   continues that ground from the photograph's own outermost pixels; a photograph whose gap edges carry a scene
   fills the frame and is cropped around its detail. Never stretched.
   The frame leaves room for both the fixed lean and the cylinder's perspective. */
(function () {
  'use strict';
  const LEAN=-.065, FRAME=1920/1078;
  // How each photograph meets the frame, read once from a small copy of it.
  // Only the edges the gap would touch decide: left and right for a photograph
  // narrower than the frame, top and bottom for a wider one.
  const fits = new Map(), COVER = {mode:'cover', fill:[0,0,0,0], position:[.5,.5]};
  function fitFor(image) {
    const key = image.currentSrc || image.src;
    if (fits.has(key)) return fits.get(key);
    const width = image.naturalWidth, height = image.naturalHeight;
    if (!(width > 0 && height > 0)) return COVER;
    const relative = width / height / FRAME;
    let fit = COVER;
    // Within 4% of the frame's shape the crop is a sliver; fill it.
    if (Math.abs(relative - 1) < .04) { fits.set(key, fit); return fit; }
    try {
      const scale = Math.min(1, 320 / Math.max(width, height)), W = Math.max(8, Math.round(width * scale)), H = Math.max(8, Math.round(height * scale));
      const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
      const context = canvas.getContext('2d', {willReadFrequently: true}); context.drawImage(image, 0, 0, W, H);
      const data = context.getImageData(0, 0, W, H).data, at = (x, y) => (y * W + x) * 4;
      const tall = relative < 1, deep = tall ? W : H;
      // Mean colour and spread of one line parallel to a gap edge, depth i in.
      const edge = (side, i = 0) => {
        const pixels = [];
        for (let j = 0; j < (tall ? H : W); j++) {
          const x = tall ? (side ? W - 1 - i : i) : j, y = tall ? j : (side ? H - 1 - i : i);
          const p = at(x, y); pixels.push([data[p], data[p+1], data[p+2], data[p+3]]);
        }
        const mean = [0,1,2,3].map(k => pixels.reduce((sum, p) => sum + p[k], 0) / pixels.length);
        const spread = Math.sqrt(pixels.reduce((sum, p) => sum + (p[0]-mean[0])**2 + (p[1]-mean[1])**2 + (p[2]-mean[2])**2, 0) / pixels.length);
        return {mean, spread, pixels};
      };
      const a = edge(0), b = edge(1);
      const apart = Math.hypot(a.mean[0]-b.mean[0], a.mean[1]-b.mean[1], a.mean[2]-b.mean[2]);
      if (a.spread < 16 && b.spread < 16 && apart < 24) {
        // How far in the ground stays clean on each side, up to 10%: the gap
        // mirrors that strip, so textures and gradients carry on unbroken and
        // a product near the edge is never reflected.
        const clean = (side, first) => {
          let i = 1;
          for (; i < deep * .1; i++) {
            // Compared point by point (softened over nine neighbours, which evens out a
            // fine texture such as linen but not a shape), so even
            // a small intrusion, the tip of a pouch, ends the strip.
            const line = edge(side, i), p = line.pixels, q = first.pixels, n = p.length;
            let worst = 0;
            for (let j = 0; j < n; j++) {
              let d = 0;
              for (let k = Math.max(0, j - 4); k <= Math.min(n - 1, j + 4); k++) d += Math.abs(p[k][0]-q[k][0]) + Math.abs(p[k][1]-q[k][1]) + Math.abs(p[k][2]-q[k][2]);
              worst = Math.max(worst, d / (Math.min(n - 1, j + 4) - Math.max(0, j - 4) + 1));
            }
            if (worst > 30) break;
          }
          return i / deep;
        };
        // A plain light ground (a white studio sweep) needs no mirror: it is filled
        // with its own colour, the median of the edge, which a blade tip or a
        // banner touching the edge cannot move. Mirroring is kept for grounds
        // with texture or shading. On a light ground a product touching the
        // edge is also filled, or the mirror repeats it as a dotted line.
        const ground = line => [0,1,2,3].map(k => line.pixels.map(p => p[k]).sort((x, y) => x - y)[line.pixels.length >> 1]);
        const touches = (line, colour) => line.pixels.some((_, j, p) => {
          let d = 0, n = 0;
          for (let k = Math.max(0, j - 2); k <= Math.min(p.length - 1, j + 2); k++, n++) d += Math.abs(p[k][0]-colour[0]) + Math.abs(p[k][1]-colour[1]) + Math.abs(p[k][2]-colour[2]);
          return d / n > 40;
        });
        const scatter = (line, colour) => { const d = line.pixels.map(p => Math.abs(p[0]-colour[0]) + Math.abs(p[1]-colour[1]) + Math.abs(p[2]-colour[2])).sort((x, y) => x - y); return d[d.length >> 1]; };
        // Plain also means even along its length: a vignette darkening towards
        // the corners needs the mirror, or the photograph's edge shows as a panel.
        // A vignette shifts much of the edge; a local mark (speed lines, a
        // shadow) shifts one or two of its eight sections and stays plain.
        const even = (line, colour) => { const n = line.pixels.length, total = colour[0] + colour[1] + colour[2]; return [0,1,2,3,4,5,6,7].filter(k => { const p = ground({pixels: line.pixels.slice(Math.floor(k * n / 8), Math.max(Math.floor(k * n / 8) + 1, Math.floor((k + 1) * n / 8)))}); return Math.abs(p[0] + p[1] + p[2] - total) > 9; }).length < 3; };
        // The ground is read two pixels in: some photographs carry a darker or
        // lighter outermost column, which would show as a band beside the fill.
        // Dark and coloured grounds keep the mirror: several are framed or
        // shaded in ways a single colour shows up as a panel (zircon-z30).
        const sides = [a, b].map((line, side) => { const inner = edge(side, Math.min(2, deep - 1)), colour = ground(inner); const light = colour.slice(0, 3).every(v => v > 200); return {colour, mirror: light && ((scatter(inner, colour) < 6 && even(inner, colour)) || touches(line, ground(line))) ? 0 : clean(side, line)}; });
        fit = {mode:'contain', fill:[0,1,2,3].map(k => (a.mean[k] + b.mean[k]) / 510), position:[.5,.5], mirror:sides.map(side => side.mirror), fills:sides.map(side => side.colour.map(v => v / 255)), tall};
      } else {
        // Crop around the detail: the centre of the photograph's edge energy
        // along the axis that is cropped, holding the window inside it.
        const long = tall ? H : W, weights = new Array(long).fill(0);
        for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
          const p = at(x, y), l = at(x-1, y), u = at(x, y-1);
          const energy = Math.abs(data[p]-data[l]) + Math.abs(data[p+1]-data[l+1]) + Math.abs(data[p+2]-data[l+2]) + Math.abs(data[p]-data[u]) + Math.abs(data[p+1]-data[u+1]) + Math.abs(data[p+2]-data[u+2]);
          weights[tall ? y : x] += energy;
        }
        const total = weights.reduce((sum, w) => sum + w, 0) || 1;
        const centre = weights.reduce((sum, w, i) => sum + w * (i + .5), 0) / total / long;
        const keep = tall ? relative : 1 / relative, start = Math.min(1 - keep, Math.max(0, centre - keep / 2));
        const along = keep < 1 ? start / (1 - keep) : .5;
        fit = {mode:'cover', fill:[0,0,0,0], position: tall ? [.5, along] : [along, .5]};
      }
    } catch (_) { fit = COVER; }
    fits.set(key, fit);
    return fit;
  }
  // The flat stand-ins and gallery layers show the same fit as the cylinder.
  function applyFit(image, fit) {
    image.style.setProperty('--pdp-img-fit', fit.mode);
    image.style.setProperty('--pdp-img-position', (fit.position[0]*100).toFixed(2) + '% ' + (fit.position[1]*100).toFixed(2) + '%');
    image.style.setProperty('--pdp-img-fill', fit.mode === 'contain' ? 'rgba(' + fit.fill.slice(0,3).map(v => Math.round(v*255)).join(',') + ',' + fit.fill[3].toFixed(3) + ')' : 'transparent');
  }
  window.SuvornaProductScene = {
    fitFor, applyFit,
    prepare(art) {
      if (!art) return FRAME;
      art.querySelectorAll('[data-media-type="image"] img').forEach(image => {
        // Width/height attributes are available even for hidden, lazy images.
        const media = image.closest('[data-media-type]');
        const width = Number(media.dataset.imageWidth) || Number(image.getAttribute('width')) || image.naturalWidth;
        const height = Number(media.dataset.imageHeight) || Number(image.getAttribute('height')) || image.naturalHeight;
        if (width > 0 && height > 0) image.style.setProperty('--pdp-image-aspect', width/height);
        if (image.complete && image.naturalWidth) applyFit(image, fitFor(image));
        else image.addEventListener('load', () => applyFit(image, fitFor(image)), {once:true});
      });
      art.style.setProperty('--pdp-image-fit', 'cover');
      art.style.setProperty('--pdp-frame-size', 92);
      art.style.setProperty('--pdp-frame-aspect', FRAME);
      art.style.setProperty('--pdp-frame-horizontal', FRAME * Math.cos(LEAN) + Math.abs(Math.sin(LEAN)));
      art.style.setProperty('--pdp-frame-vertical', Math.cos(LEAN) + FRAME * Math.abs(Math.sin(LEAN)));
      return FRAME;
    },
    create(art) {
      const THREE = window.THREE;
      if (!THREE || !art?.querySelector('[data-media-type="image"] img')) return null;
      const frameAspect = this.prepare(art);
      let renderer;
      try { renderer = new THREE.WebGLRenderer({alpha:true,antialias:true,powerPreference:'low-power'}); } catch (_) { return null; }
      const canvas=renderer.domElement; canvas.setAttribute('aria-hidden','true'); art.append(canvas);
      renderer.setPixelRatio(Math.min(devicePixelRatio||1,1.5)); renderer.setClearColor(0xffffff,0);
      const scene=new THREE.Scene(),camera=new THREE.PerspectiveCamera(35,1,.1,30),geometry=new THREE.PlaneGeometry(1,1,1,64);
      camera.position.z=4;
      const records=new Map(),loader=new THREE.TextureLoader(),travel={value:1}; loader.setCrossOrigin('anonymous');
      let current,outgoing,tween,sequence=null,width=0,height=0,generation=0,disposed=false,lost=false,enabled=false,dirty=true,direction=1;
      const fallback=()=>art.classList.remove('has-scene');
      const onLost=e=>{e.preventDefault();lost=true;fallback();};
      const onRestored=()=>{lost=false;dirty=true;};
      canvas.addEventListener('webglcontextlost',onLost); canvas.addEventListener('webglcontextrestored',onRestored);
      const resize=new ResizeObserver(entries=>{
        ({width,height}=entries[0].contentRect); if(!width||!height)return;
        renderer.setSize(width,height,false);camera.aspect=width/height;camera.updateProjectionMatrix();dirty=true;
      });resize.observe(art);
      function release(record){if(record.mesh){scene.remove(record.mesh);record.mesh.material.dispose();record.texture.dispose();}}
      function prune(){for(const [src,record] of records){if(records.size<=4)break;if(record!==current&&record!==outgoing&&record.mesh){release(record);records.delete(src);}}}
      function load(src){
        if(records.has(src))return records.get(src).promise;
        const record={}; records.set(src,record);
        record.promise=new Promise(resolve=>loader.load(src,texture=>{
          if(disposed){texture.dispose();resolve(null);return;}
          record.texture=texture;record.aspect=texture.image.width/texture.image.height;record.fit=fitFor(texture.image);
          texture.minFilter=THREE.LinearFilter;texture.generateMipmaps=false;texture.anisotropy=Math.min(4,renderer.capabilities.getMaxAnisotropy());
          const material=new THREE.ShaderMaterial({
            uniforms:{uMap:{value:texture},uFit:{value:new THREE.Vector2(1,1)},uDimensions:{value:new THREE.Vector2(1,1)},uRadius:{value:4},uRoll:{value:0},uOpacity:{value:1},uCenter:{value:new THREE.Vector2(.5,.5)},uContain:{value:0},uMirror:{value:new THREE.Vector4(.001,.001,.001,.001)},uFillLo:{value:new THREE.Vector4(0,0,0,0)},uFillHi:{value:new THREE.Vector4(0,0,0,0)}},
            vertexShader:`uniform vec2 uDimensions;uniform float uRadius;uniform float uRoll;varying vec2 vUv;
              void main(){vUv=uv;vec3 p=position;p.xy*=uDimensions;float a=p.y/uRadius+uRoll;p.y=uRadius*sin(a);p.z=uRadius*(1.0-cos(a));gl_Position=projectionMatrix*modelViewMatrix*vec4(p,1.0);}`,
            fragmentShader:`uniform sampler2D uMap;uniform vec2 uFit;uniform vec2 uCenter;uniform float uContain;uniform vec4 uMirror;uniform vec4 uFillLo;uniform vec4 uFillHi;uniform float uOpacity;varying vec2 vUv;
              void main(){vec2 uv=(vUv-.5)*uFit+uCenter;vec2 inside=min(uv,1.0-uv);float room=min(inside.x,inside.y);vec4 c;
                // Contain folds the clean edge strip (uMirror: left, right, bottom,
                // top) back and forth across the gap, so the ground runs on unbroken;
                // a side whose strip is 0 is filled with its ground (uFillLo/Hi).
                bool plain=false;vec4 fill=vec4(0.0);
                if(uv.x<0.0&&uMirror.x<=0.0){plain=true;fill=uFillLo;}
                if(uv.x>1.0&&uMirror.y<=0.0){plain=true;fill=uFillHi;}
                if(uv.y<0.0&&uMirror.z<=0.0){plain=true;fill=uFillLo;}
                if(uv.y>1.0&&uMirror.w<=0.0){plain=true;fill=uFillHi;}
                if(uContain<.5&&room<0.0)discard;
                if(uv.x<0.0){float t=mod(-uv.x,2.0*uMirror.x);uv.x=t<uMirror.x?t:2.0*uMirror.x-t;}
                if(uv.x>1.0){float t=mod(uv.x-1.0,2.0*uMirror.y);uv.x=1.0-(t<uMirror.y?t:2.0*uMirror.y-t);}
                if(uv.y<0.0){float t=mod(-uv.y,2.0*uMirror.z);uv.y=t<uMirror.z?t:2.0*uMirror.z-t;}
                if(uv.y>1.0){float t=mod(uv.y-1.0,2.0*uMirror.w);uv.y=1.0-(t<uMirror.w?t:2.0*uMirror.w-t);}
                c=plain?fill:texture2D(uMap,clamp(uv,0.0,1.0));
                float edge=abs(vUv.y-.5)*2.0;float ink=1.0-min(c.r,min(c.g,c.b));c.rgb*=1.0-.035*edge*edge*ink;gl_FragColor=vec4(c.rgb,c.a*uOpacity);}`,
            side:THREE.DoubleSide,transparent:true,depthWrite:false,depthTest:false
          });
          record.mesh=new THREE.Mesh(geometry,material);record.mesh.frustumCulled=false;record.mesh.visible=false;record.mesh.rotation.z=LEAN;scene.add(record.mesh);resolve(record);
        },undefined,()=>{records.delete(src);resolve(null);}));return record.promise;
      }
      function setSource(src,options={}){
        const request=++generation;
        tween?.kill(); sequence=null;
        if(!src){enabled=false;fallback();return false;}
        enabled=true;
        const sources=[...(options.via || []),src];
        Promise.all(sources.map(load)).then(loaded=>{
          if(disposed||request!==generation)return;
          const record=loaded[loaded.length-1];
          if(loaded.some(item=>!item)){enabled=false;fallback();options.onComplete?.();return;}
          tween?.kill();outgoing=current===record?null:current;current=record;direction=options.direction<0?-1:1;
          const animate=outgoing&&options.animate!==false&&window.gsap&&!matchMedia('(prefers-reduced-motion: reduce)').matches;
          sequence=animate?[outgoing,...loaded]:null;
          const steps=loaded.length;
          travel.value=animate?0:steps;dirty=true;
          if(animate)tween=gsap.to(travel,{value:steps,duration:options.duration || .88,ease:'power2.inOut',onUpdate:()=>{dirty=true;options.onStep?.(Math.round(travel.value));},onComplete:()=>{sequence=null;outgoing=null;dirty=true;prune();options.onComplete?.();}});
          else{outgoing=null;prune();options.onComplete?.();}
        });return !lost;
      }
      function draw(record,roll,order){
        if(!record)return;
        const viewH=8*Math.tan(35*Math.PI/360),viewW=viewH*camera.aspect,c=Math.cos(LEAN),s=Math.abs(Math.sin(LEAN));
        const frameH=Math.min(viewH*.92/(c+frameAspect*s),viewW*.92/(frameAspect*c+s));
        const w=frameH*frameAspect,h=frameH;
        const mesh=record.mesh,u=mesh.material.uniforms;mesh.visible=true;mesh.renderOrder=order;
        u.uDimensions.value.set(w,h);u.uRadius.value=frameH*1.4;
        // Contain shows the whole photograph on its own ground; cover crops it
        // around its detail (fitFor). Texture v runs bottom to top.
        const ratio=w/h/record.aspect,fit=record.fit||COVER,contain=fit.mode==='contain';
        u.uFit.value.set(contain?Math.max(1,ratio):Math.min(1,ratio),contain?Math.max(1,1/ratio):Math.min(1,1/ratio));
        const fx=u.uFit.value.x,fy=u.uFit.value.y;
        u.uCenter.value.set(contain?.5:fx/2+fit.position[0]*(1-fx),contain?.5:1-(fy/2+fit.position[1]*(1-fy)));
        u.uContain.value=contain?1:0;
        // Texture v runs bottom to top: the photograph's bottom edge is v=0.
        const m=fit.mirror||[.001,.001],fills=fit.fills||[[0,0,0,0],[0,0,0,0]];u.uMirror.value.set(...(fit.tall?[m[0],m[1],.001,.001]:[.001,.001,m[1],m[0]]));
        u.uFillLo.value.set(...fills[fit.tall?0:1]);u.uFillHi.value.set(...fills[fit.tall?1:0]);
        u.uRoll.value=roll;
        u.uOpacity.value=1-THREE.MathUtils.smoothstep(Math.abs(roll),.58,1);
      }
      const image=art.querySelector('[data-media-type="image"]:not([hidden]) img');
      if(image)setSource(image.currentSrc||image.src,{animate:false});
      return {setSource,render(){
        if(!dirty||!enabled||!current||disposed||lost||!width||!height)return;
        for(const record of records.values())if(record.mesh)record.mesh.visible=false;
        if(sequence)sequence.forEach((record,index)=>{const roll=(travel.value-index)*direction;if(Math.abs(roll)<1.05)draw(record,roll,index);});
        else draw(current,0,1);
        try{renderer.render(scene,camera);art.classList.add('has-scene');dirty=false;}catch(_){lost=true;fallback();}
      },dispose(){disposed=true;++generation;tween?.kill();fallback();resize.disconnect();canvas.removeEventListener('webglcontextlost',onLost);canvas.removeEventListener('webglcontextrestored',onRestored);records.forEach(release);records.clear();geometry.dispose();renderer.dispose();canvas.remove();}};
    }
  };
})();
